import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { ScreenHeader } from '../components/ScreenHeader';
import { AppModal, ModalType } from '../components/AppModal';
import { SkeletonCompactList } from '../components/ShimmerLoading';
import ApiService from '../services/ApiService';
import { DairyPurchase } from '../models/types';
import { formatCurrency, formatDate } from '../utils/helpers';

const DairyPurchasesScreen = ({ navigation }: any) => {
    const [purchases, setPurchases] = useState<DairyPurchase[]>([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
    }>({ visible: false, type: 'info', title: '', message: '' });

    const loadPurchases = async () => {
        setLoading(true);
        try {
            const data = await ApiService.getDairyPurchases();
            setPurchases(data);

            const total = await ApiService.getDairyPurchasesTotal();
            setTotalAmount(total);
        } catch (error) {
            console.error('Error loading dairy purchases:', error);
        } finally {
            setLoading(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadPurchases();
        }, [])
    );

    const handleDeletePurchase = (purchase: DairyPurchase) => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Purchase',
            message: `Are you sure you want to delete "${purchase.itemName}"?`,
            onConfirm: async () => {
                setDeleteLoading(true);
                try {
                    await ApiService.deleteDairyPurchase(purchase.id);
                    setModal(prev => ({ ...prev, visible: false }));
                    loadPurchases();
                } catch (error) {
                    console.error('Error deleting purchase:', error);
                    setModal({
                        visible: true,
                        type: 'error',
                        title: 'Error',
                        message: 'Failed to delete purchase.',
                    });
                } finally {
                    setDeleteLoading(false);
                }
            },
        });
    };

    const renderPurchaseItem = ({ item }: { item: DairyPurchase }) => {
        return (
            <TouchableOpacity
                style={styles.purchaseCard}
                onPress={() => navigation.navigate('AddEditDairyPurchase', { purchase: item })}
                activeOpacity={0.7}
            >
                <View style={styles.iconContainer}>
                    <Icon name="cart" size={28} color="#4CAF50" />
                </View>

                <View style={styles.purchaseContent}>
                    <View style={styles.purchaseHeader}>
                        <Text style={styles.itemName}>{item.itemName}</Text>
                        <Text style={styles.price}>{formatCurrency(item.totalAmount)}</Text>
                    </View>

                    <View style={styles.purchaseDetails}>
                        <View style={styles.detailRow}>
                            <Icon name="scale" size={14} color={theme.colors.textSecondary} />
                            <Text style={styles.source}>{item.quantity} x {formatCurrency(item.pricePerUnit)}</Text>
                        </View>
                        <View style={styles.detailRow}>
                            <Icon name="calendar" size={14} color={theme.colors.textSecondary} />
                            <Text style={styles.date}>{formatDate(item.date)}</Text>
                        </View>
                    </View>

                    {item.source && (
                        <View style={[styles.detailRow, { marginTop: 4 }]}>
                            <Icon name="store" size={14} color={theme.colors.textSecondary} />
                            <Text style={styles.source}>{item.source}</Text>
                        </View>
                    )}

                    {item.notes && (
                        <Text style={styles.notes} numberOfLines={1}>
                            {item.notes}
                        </Text>
                    )}
                </View>

                <TouchableOpacity
                    style={styles.deleteButton}
                    onPress={() => handleDeletePurchase(item)}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                    <Icon name="trash-can-outline" size={20} color={theme.colors.error} />
                </TouchableOpacity>
            </TouchableOpacity>
        );
    };

    const renderEmpty = () => (
        <View style={styles.emptyContainer}>
            <Icon name="cart-outline" size={64} color={theme.colors.textLight} />
            <Text style={styles.emptyTitle}>No Purchases Found</Text>
            <Text style={styles.emptyText}>
                Add dairy-related purchases to track expenses
            </Text>
        </View>
    );

    return (
        <SafeAreaView style={styles.container} edges={['top']}>
            <ScreenHeader title="Dairy Purchases" showBackButton={true} navigation={navigation} />

            {/* Total Summary Card */}
            <View style={styles.summaryCard}>
                <View style={styles.summaryIconBg}>
                    <Icon name="currency-inr" size={24} color="#4CAF50" />
                </View>
                <View style={styles.summaryContent}>
                    <Text style={styles.summaryLabel}>Total Expenses</Text>
                    <Text style={styles.summaryAmount}>{formatCurrency(totalAmount)}</Text>
                </View>
                <View style={styles.summaryBadge}>
                    <Icon name="package-variant" size={16} color={theme.colors.primary} />
                    <Text style={styles.summaryCount}>{purchases.length}</Text>
                </View>
            </View>

            {/* Purchase List */}
            {loading ? (
                <SkeletonCompactList itemCount={8} />
            ) : (
                <FlashList
                    data={purchases}
                    keyExtractor={(item) => item.id}
                    renderItem={renderPurchaseItem}
                    contentContainerStyle={styles.listContent}
                    ListEmptyComponent={renderEmpty}
                    refreshing={false}
                    onRefresh={loadPurchases}
                />
            )}

            {/* FAB */}
            <TouchableOpacity
                style={styles.fab}
                onPress={() => navigation.navigate('AddEditDairyPurchase', {})}
            >
                <Icon name="plus" size={28} color="#fff" />
            </TouchableOpacity>

            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
                loading={deleteLoading}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    summaryCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        padding: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        ...theme.shadows.sm,
    },
    summaryIconBg: {
        width: 48,
        height: 48,
        borderRadius: theme.borderRadius.md,
        backgroundColor: '#E8F5E9',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    summaryContent: {
        flex: 1,
    },
    summaryLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
        marginBottom: 2,
    },
    summaryAmount: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
    },
    summaryBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.primary + '15',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: theme.borderRadius.round,
        gap: 4,
    },
    summaryCount: {
        ...theme.typography.caption,
        color: theme.colors.primary,
        fontWeight: '700',
    },
    listContent: {
        paddingHorizontal: theme.spacing.md,
        paddingBottom: 100,
    },
    purchaseCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        borderLeftWidth: 4,
        borderLeftColor: '#4CAF50',
        ...theme.shadows.sm,
    },
    iconContainer: {
        width: 48,
        height: 48,
        borderRadius: theme.borderRadius.md,
        backgroundColor: '#E8F5E9',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    purchaseContent: {
        flex: 1,
    },
    purchaseHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 6,
    },
    itemName: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
        flex: 1,
        marginRight: theme.spacing.sm,
    },
    price: {
        ...theme.typography.body1,
        color: '#4CAF50',
        fontWeight: '700',
    },
    purchaseDetails: {
        flexDirection: 'row',
        gap: theme.spacing.md,
        marginBottom: 4,
    },
    detailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    source: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    date: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    notes: {
        ...theme.typography.caption,
        color: theme.colors.textLight,
        fontStyle: 'italic',
        marginTop: 2,
    },
    deleteButton: {
        padding: theme.spacing.xs,
    },
    emptyContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: 100,
    },
    emptyTitle: {
        ...theme.typography.h5,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.md,
    },
    emptyText: {
        ...theme.typography.body2,
        color: theme.colors.textLight,
        marginTop: theme.spacing.xs,
    },
    fab: {
        position: 'absolute',
        right: theme.spacing.lg,
        bottom: theme.spacing.lg,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme.colors.primary,
        alignItems: 'center',
        justifyContent: 'center',
        ...theme.shadows.md,
    },
});

export default DairyPurchasesScreen;
